"use client";

import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

interface NavSvgIconProps {
  size?: number;
  className?: string;
  play?: boolean;
}

const CX = 13.08;
const CY = 13.08;
const RY = 12.93;
const BASE_RX = 12.93; // matches boundary radius so meridian merges with edge at appearance/disappearance

const TOP_Y = (CY - RY).toFixed(3);
const BOTTOM_Y = (CY + RY).toFixed(3);

const PHANTOM_COUNT = 8;
const PHASE_STEP = (Math.PI * 2) / PHANTOM_COUNT;

// Initial path used for SSR + reduced-motion: full ellipse outline matching uploaded SVG (rx=6.76)
const INITIAL_D = `M ${CX} ${TOP_Y} A 6.76 ${RY} 0 0 1 ${CX} ${BOTTOM_Y} A 6.76 ${RY} 0 0 1 ${CX} ${TOP_Y}`;

export function InsightsIcon({ size = 66, className, play = true }: NavSvgIconProps) {
  const containerRef = useRef<SVGSVGElement | null>(null);
  const meridianRefs = useRef<(SVGPathElement | null)[]>(
    Array(PHANTOM_COUNT).fill(null)
  );

  useGSAP(
    () => {
      if (!play) return;
      const meridians = meridianRefs.current.filter(
        (el): el is SVGPathElement => el !== null
      );
      if (meridians.length !== PHANTOM_COUNT) return;

      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const state = { angle: 0 };
        gsap.to(state, {
          angle: Math.PI * 2,
          duration: 15,
          repeat: -1,
          ease: "none",
          onUpdate: () => {
            meridians.forEach((m, i) => {
              const phase = state.angle + i * PHASE_STEP;
              const sinPhase = Math.sin(phase);
              const cosPhase = Math.cos(phase);

              if (sinPhase <= 0) {
                m.setAttribute("d", "");
                return;
              }
              const rx = Math.abs(cosPhase) * BASE_RX;
              if (rx < 0.05) {
                m.setAttribute("d", `M ${CX} ${TOP_Y} L ${CX} ${BOTTOM_Y}`);
                return;
              }
              const sweep = cosPhase > 0 ? 1 : 0;
              m.setAttribute(
                "d",
                `M ${CX} ${TOP_Y} A ${rx.toFixed(3)} ${RY} 0 0 ${sweep} ${CX} ${BOTTOM_Y}`
              );
            });
          },
        });
      });

      return () => mm.revert();
    },
    { scope: containerRef, dependencies: [play] }
  );

  return (
    <svg
      ref={containerRef}
      viewBox="0 0 26.16 26.16"
      width={size}
      height={size}
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="0.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="13.08" cy="13.08" r="12.93" />
      <line x1="26.01" y1="13.08" x2="0.15" y2="13.08" />
      <line x1="13.08" y1="26.01" x2="13.08" y2="0.15" />
      {Array.from({ length: PHANTOM_COUNT }, (_, i) => (
        <path
          key={i}
          ref={(el) => {
            meridianRefs.current[i] = el;
          }}
          d={INITIAL_D}
        />
      ))}
    </svg>
  );
}
